import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Lock, Zap, Crown, BarChart3, TrendingUp, Users, CreditCard, Scan, Briefcase, UserPlus } from 'lucide-react';

const UpgradeModal = ({ open, onOpenChange, cardId = null, limitType = 'analytics', currentUsage = 0, maxLimit = 0, nonPremiumCount = 0 }) => {
  const navigate = useNavigate();

  const limitInfo = {
    analytics: {
      title: 'Unlock Analytics & Insights',
      subtitle: 'Upgrade to Pro or Business to access powerful analytics',
      icon: Lock,
      showCardPremium: false
    },
    card: {
      title: 'Card Premium Required',
      subtitle: nonPremiumCount > 0 
        ? `You have ${nonPremiumCount} card(s) without active premium. Please purchase Card Premium for your existing cards before creating new ones.`
        : `Your card's premium has expired. Purchase Card Premium to create more cards.`,
      icon: Crown,
      showCardPremium: true
    },
    pipeline: {
      title: 'Pipeline Limit Reached',
      subtitle: `You've created ${currentUsage} pipeline. Free plan includes ${maxLimit} pipeline only.`,
      icon: Crown,
      showCardPremium: false
    },
    deal: {
      title: 'Deal Limit Reached',
      subtitle: `You've created ${currentUsage} deals. Free plan allows maximum ${maxLimit} deals.`,
      icon: TrendingUp,
      showCardPremium: false
    },
    scan: {
      title: 'Scan Limit Reached',
      subtitle: `You've used ${currentUsage} scans. Free plan includes ${maxLimit} physical card scans.`,
      icon: BarChart3,
      showCardPremium: false
    },
    teams: {
      title: 'Team Creation - Business Plan Only',
      subtitle: 'Team management is exclusively available on the Business plan. Upgrade to collaborate with your team.',
      icon: Users,
      showCardPremium: false
    }
  };

  const info = limitInfo[limitType] || limitInfo.analytics;
  const DisplayIcon = info.icon;

  const handleUpgrade = () => {
    // Store the card ID they were trying to view in session storage
    if (cardId) {
      sessionStorage.setItem('pendingAnalyticsCard', cardId);
    }
    onOpenChange(false);
    if (info.showCardPremium) {
      navigate('/profile');  // Go to profile to buy card premium
    } else {
      navigate('/pricing');  // Go to pricing for account subscription
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <div className="flex items-center justify-center mb-4">
            <div className="w-20 h-20 rounded-full bg-gradient-to-r from-amber-100 to-teal-100 flex items-center justify-center">
              <DisplayIcon className="w-10 h-10 text-amber-600" />
            </div>
          </div>
          <DialogTitle className="text-center text-2xl sm:text-3xl font-bold">
            {info.title}
          </DialogTitle>
          <p className="text-center text-gray-600 mt-2">
            {info.subtitle}
          </p>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {info.showCardPremium ? (
            // Card Premium section
            <>
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <Crown className="w-5 h-5 text-amber-600" />
                  Card Premium includes:
                </h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Premium card templates</li>
                  <li>• Custom logo on card</li>
                  <li>• No Piper Card branding</li>
                  <li>• Priority support</li>
                </ul>
              </div>
              
              <div className="bg-gradient-to-r from-amber-50 to-pink-50 rounded-lg p-5 border-2 border-amber-200">
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-2">Go to your Profile to purchase Card Premium</p>
                  <Button
                    onClick={handleUpgrade}
                    className="bg-gradient-to-r from-amber-600 to-pink-600 hover:from-amber-700 hover:to-pink-700 text-white font-semibold px-8 py-3"
                  >
                    <Crown className="w-4 h-4 mr-2" />
                    Go to My Cards
                  </Button>
                </div>
              </div>
            </>
          ) : (
            // Account subscription section (original content)
            <>
              {/* Features Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Unlimited Cards</h4>
                    <p className="text-sm text-gray-600">Create unlimited virtual cards</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 bg-teal-50 rounded-lg border border-teal-200">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                    <Scan className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Card Scanning</h4>
                    <p className="text-sm text-gray-600">Unlimited physical card scans</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <Briefcase className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Full CRM</h4>
                    <p className="text-sm text-gray-600">Unlimited deals & pipelines</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 bg-teal-50 rounded-lg border border-teal-200">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                    <UserPlus className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Team Management</h4>
                    <p className="text-sm text-gray-600">Collaborate with your team</p>
                  </div>
                </div>
              </div>

              {/* Pricing Plans */}
              <div className="space-y-3">
                {/* Pro Plan */}
                <div className="bg-gradient-to-r from-amber-50 to-pink-50 rounded-lg p-5 border-2 border-amber-200">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Crown className="w-5 h-5 text-amber-600" />
                        <h3 className="font-bold text-lg">Pro Plan</h3>
                        <Badge className="bg-amber-600">Popular</Badge>
                      </div>
                      <p className="text-sm text-gray-600">Perfect for individuals</p>
                      <p className="text-lg font-bold text-amber-600 mt-1">₹299/person/month</p>
                    </div>
                    <Button
                      onClick={handleUpgrade}
                      className="w-full sm:w-auto bg-gradient-to-r from-amber-600 to-pink-600 hover:from-amber-700 hover:to-pink-700 text-white font-semibold px-6 py-3"
                    >
                      Choose Pro
                    </Button>
                  </div>
                </div>

                {/* Business Plan */}
                <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-lg p-5 border-2 border-teal-200">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Users className="w-5 h-5 text-teal-600" />
                        <h3 className="font-bold text-lg">Business Plan</h3>
                        <Badge className="bg-teal-600">Best Value</Badge>
                      </div>
                      <p className="text-sm text-gray-600">For teams (3+ members)</p>
                      <p className="text-lg font-bold text-teal-600 mt-1">₹249/member/month</p>
                    </div>
                    <Button
                      onClick={handleUpgrade}
                      className="w-full sm:w-auto bg-gradient-to-r from-teal-600 to-blue-600 hover:from-teal-700 hover:to-blue-700 text-white font-semibold px-6 py-3"
                    >
                      Choose Business
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Alternative CTA */}
          <div className="text-center">
            <Button
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="text-gray-600"
            >
              Maybe Later
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UpgradeModal;
